import React from 'react';
import clsx from 'clsx';

import { countries } from 'utils/formData';
import { texts } from 'utils/languages/fr';

import { TableRowButtons } from './TableRowButtons';
import {
    NativeDatePicker,
    CustomSelect,
    CustomTextField,
    BasicAddressForm,
    BasicTableCellAddressForm,
    RadioLabel,
} from 'components';
import { TableCell, RadioGroup, Box, MenuItem, TableRow, InputBase } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    flexContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
    },
    tableCell: {
        border: '1px solid #ccc',
        textAlign: 'center',
        padding: 0,
        margin: 0,
        height: 1,
        width: 150,
        minWidth: 150,
        '&:nth-child(3)': {
            width: 250,
            minWidth: 250,
        },
        '&:nth-child(4)': {
            width: 190,
            minWidth: 175,
        },
    },
    input: {
        padding: 0,
        margin: 0,
        minHeight: 65,
        height: '100%',
        width: '100%',
    },
    dateInput: {
        flexDirection: 'row',
        marginBottom: 0,
        height: '100%',
        borderBottom: `1px solid ${theme.palette.common.black}`,
        padding: theme.spacing(),
    },
    textInput: {
        padding: '0px 5px',
        margin: 0,
        minHeight: 65,
        borderBottom: '1px solid #0000006b',
        width: '100%',
        height: '100%',
    },
    textInputHover: {
        '&:hover': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
    },
    selectInput: {
        marginBottom: 0,
        height: '100%',
    },
    error: {
        border: `2px solid ${theme.palette.error.main}`,
    },
    registrationTableCell: {
        minWidth: 450,
        borderBottom: '1px solid #0000006b',
        '&:hover': {
            borderBottom: `2px solid ${theme.palette.primary.main}`,
        },
    },
    registrationRadioGroup: {
        padding: 16,
        flexDirection: 'column',
        textAlign: 'left',
    },
    disabled: {
        backgroundColor: ' #F8F8F8',
    },
}));

const renderOption = opt => (
    <MenuItem key={opt.id} value={opt.id}>
        {opt.name}
    </MenuItem>
);

export const CommonPPTableRows = ({
    values: { lastname, firstname, address, birthDate, cityOfBirth, nationality, nif },
    handleChange,
    handleAddressChange,
    handleDateChange,
    hasNationality,
    hasNIF,
    errors,
}) => {
    const classes = useStyles();
    return (
        <>
            <TableCell classes={{ root: classes.tableCell }}>
                <InputBase
                    className={clsx(
                        classes.textInput,
                        errors?.lastname ? classes.error : classes.textInputHover
                    )}
                    name="lastname"
                    onChange={handleChange}
                    value={lastname}
                    multiline
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <InputBase
                    className={clsx(
                        classes.textInput,
                        errors?.firstname ? classes.error : classes.textInputHover
                    )}
                    name="firstname"
                    onChange={handleChange}
                    value={firstname}
                    multiline
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <BasicTableCellAddressForm
                    onAddressChange={handleAddressChange}
                    address={address}
                    placeholder={texts.ADRESSE_DU_DOMICILE_PERSONNEL}
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <NativeDatePicker
                    className={classes.dateInput}
                    name="birthDate"
                    handleDateChange={handleDateChange}
                    value={birthDate}
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <InputBase
                    className={clsx(classes.textInput, classes.textInputHover)}
                    name="cityOfBirth"
                    onChange={handleChange}
                    value={cityOfBirth}
                    multiline
                />
            </TableCell>
            {hasNationality && (
                <TableCell classes={{ root: classes.tableCell }}>
                    <CustomSelect
                        inputStyle={classes.selectInput}
                        className={classes.input}
                        name="nationality"
                        onChange={handleChange}
                        value={nationality}
                        variant="standard"
                    >
                        {countries.map(renderOption)}
                    </CustomSelect>
                </TableCell>
            )}
            {hasNIF && (
                <TableCell classes={{ root: classes.tableCell }}>
                    <InputBase
                        className={clsx(classes.textInput, classes.textInputHover)}
                        name="nif"
                        onChange={handleChange}
                        value={nif}
                        multiline
                    />
                </TableCell>
            )}
        </>
    );
};

export const CommonIndividualsTableRows = ({
    classes,
    values,
    index,
    handleChange,
    handleAddressChange,
    handleDateChange,
    removeRow,
    hasNationality,
    errors,
}) => (
    <TableRow className={classes.tableRow} key={index}>
        <CommonPPTableRows
            classes={classes}
            values={values}
            handleChange={handleChange}
            handleAddressChange={handleAddressChange}
            handleDateChange={handleDateChange}
            hasNationality={hasNationality}
            errors={errors}
        />
        <TableRowButtons index={index} removeRow={removeRow} />
    </TableRow>
);

export const CommonPMTabelRows = ({
    values: {
        corporateName,
        legalForm,
        address,
        registration,
        RCSNumber,
        registrationNumber,
        lastname,
        firstname,
        registrationAddress,
    },
    handleChange,
    handleAddressChange,
    handleRegistrationAddressChange,
    handleRadioButtons,
    hasRegistration,
    errors,
}) => {
    const classes = useStyles();
    return (
        <>
            <TableCell classes={{ root: classes.tableCell }}>
                <InputBase
                    className={clsx(
                        classes.textInput,
                        errors?.corporateName ? classes.error : classes.textInputHover
                    )}
                    name="corporateName"
                    onChange={handleChange}
                    value={corporateName}
                    multiline
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <InputBase
                    className={clsx(classes.textInput, classes.textInputHover)}
                    name="legalForm"
                    onChange={handleChange}
                    value={legalForm}
                    multiline
                />
            </TableCell>
            <TableCell classes={{ root: classes.tableCell }}>
                <BasicTableCellAddressForm
                    onAddressChange={handleAddressChange}
                    address={address}
                    placeholder={texts.ADRESSE_DU_SIEGE_SOCIAL}
                />
            </TableCell>
            {hasRegistration && (
                <TableCell
                    classes={{
                        root: classes.tableCell,
                    }}
                >
                    <div className={classes.registrationTableCell}>
                        <RadioGroup
                            className={clsx(classes.flexContainer, classes.registrationRadioGroup)}
                            onChange={handleRadioButtons}
                        >
                            <RadioLabel
                                value="franceRegistration"
                                label={texts.IMMATRICULEE_EN_FRANCE}
                                checked={registration === 'franceRegistration'}
                                name="registration"
                            />
                            {registration === 'franceRegistration' && (
                                <CustomTextField
                                    placeholder={texts.NUMERO_RCS}
                                    name="RCSNumber"
                                    onChange={handleChange}
                                    value={RCSNumber}
                                />
                            )}
                            <RadioLabel
                                value="inEU"
                                label={texts.RELVE_DE_LA_LEGISLATION}
                                checked={registration === 'inEU'}
                                name="registration"
                            />
                            {registration === 'inEU' && (
                                <CustomTextField
                                    placeholder={texts.NUMERO_ET_LIEU}
                                    name="registrationNumber"
                                    onChange={handleChange}
                                    value={registrationNumber}
                                />
                            )}
                            <RadioLabel
                                value="nonEU"
                                label={texts.PAS_IMMATRICULE}
                                checked={registration === 'nonEU'}
                                name="registration"
                            />
                            {registration === 'nonEU' && (
                                <Box display="flex" flexDirection="column">
                                    <CustomTextField
                                        placeholder={texts.NOM}
                                        name="lastname"
                                        onChange={handleChange}
                                        value={lastname}
                                    />
                                    <CustomTextField
                                        placeholder={texts.PRENOM}
                                        name="firstname"
                                        onChange={handleChange}
                                        value={firstname}
                                    />
                                    <BasicAddressForm
                                        address={registrationAddress}
                                        onAddressChange={handleRegistrationAddressChange}
                                        placeholder={texts.ADRESSE_DU_DOMICILE_PERSONNEL}
                                    />
                                </Box>
                            )}
                        </RadioGroup>
                    </div>
                </TableCell>
            )}
        </>
    );
};
